import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Coins, GraduationCap } from "lucide-react";
import AppLayout from "../components/AppLayout";
import EventShareDialog from "../components/EventShareDialog";
import InvitePlayersDialog from "../components/InvitePlayersDialog";
import AddToCalendarButton from "../components/AddToCalendarButton";
import AttendanceWidget from "../components/AttendanceWidget";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";

export default function LessonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const fetchL = () => api.get(`/lessons/${id}`).then((r) => setL(r.data));
  useEffect(() => { fetchL(); }, [id]); // eslint-disable-line

  if (!l) return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Loading...</div></AppLayout>;
  const reg = user && l.registrations?.some((r) => r.user_id === user.id);
  const isCoach = user && (l.coach_id === user.id || user.role === "admin");
  const book = async () => {
    if (!user) { navigate("/login"); return; }
    try { await api.post(`/lessons/${id}/register`); toast.success(t("book")); fetchL(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppLayout>
      <Link to="/lessons" data-testid="back-lessons" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> {t("lessons")}</Link>
      <div className="pp-card-elevated p-8 max-w-3xl">
        <div className="text-xs text-[#00E676] uppercase tracking-wider flex items-center gap-1">
          <GraduationCap size={14} /> {l.sport?.name} · {l.lesson_type}
          {l.visibility === "private" && <span className="ms-2 pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40" data-testid="lesson-private-badge">Private</span>}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black mt-2" data-testid="lesson-title">{l.title}</h1>
        <p className="text-zinc-400 mt-3">{l.description}</p>
        <div className="mt-4 text-sm text-zinc-400">Coach: <Link to={`/coaches`} className="text-white underline">{l.coach?.first_name} {l.coach?.last_name}</Link></div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div className="pp-card p-3"><div className="text-xs text-zinc-500 uppercase">{t("date")}</div><div className="flex items-center gap-2 mt-1"><Calendar size={14} /> {l.all_day ? new Date(l.start_time).toLocaleDateString() + " · All day" : new Date(l.start_time).toLocaleString()}{l.end_time && !l.all_day ? ` – ${new Date(l.end_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}` : ""}</div></div>
          <div className="pp-card p-3"><div className="text-xs text-zinc-500 uppercase">{t("price_per_player")}</div><div className="flex items-center gap-2 mt-1"><Coins size={14} /> {l.price > 0 ? `${l.price} ${l.currency}` : t("free")}</div></div>
        </div>
        <div className="mt-6">
          <AttendanceWidget eventKind="lesson" eventId={id} />
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          {reg ? <span className="pp-chip pp-chip-active" data-testid="lesson-booked">{t("joined")}</span> :
            <button data-testid="lesson-book-btn" onClick={book} className="pp-btn-primary px-6 py-3 rounded-xl">{t("book")}</button>}
          <AddToCalendarButton
            kind="lesson"
            id={id}
            start={l.start_time}
            end={l.end_time}
            title={l.title}
            location={l.location}
            description={l.description}
            testIdSuffix="lesson"
          />
          {isCoach && (
            <>
              <InvitePlayersDialog eventKind="lesson" eventId={id} onInvited={fetchL} />
              <EventShareDialog eventKind="lesson" eventId={id} eventTitle={l.title} />
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
